import React from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, X } from 'lucide-react';

const FilterSelect = ({ label, value, options = [], onChange }) => {
    const { t } = useTranslation();
    return (
        <div className="sd-filter-field">
            <label className="sd-filter-field__label">{label}</label>
            <select
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                className="sd-filter-field__select"
            >
                <option value="">{t('systemDashboard.common.all')}</option>
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

const FilterInput = ({ label, type, value, onChange, placeholder }) => {
    const { t } = useTranslation();
    return (
        <div className="sd-filter-field">
            <label className="sd-filter-field__label">{label}</label>
            <input
                type={type}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder || t('systemDashboard.common.any')}
                className="sd-filter-field__input"
            />
        </div>
    );
};

/**
 * SmartFilters - Compact expandable tab-specific metadata filters.
 */
const SmartFilters = ({
    activeTab,
    filters,
    countryOptions,
    onChange,
    onReset,
    isOpen,
    onToggle
}) => {
    const { t } = useTranslation();

    const hasActiveSmartFilters =
        filters.gender ||
        filters.min_age ||
        filters.max_age ||
        filters.country_id ||
        filters.min_reactions ||
        filters.min_comments ||
        filters.visit_type;

    return (
        <div className="sd-smart-filters">
            <button
                onClick={onToggle}
                className={`sd-smart-filters__toggle ${isOpen || hasActiveSmartFilters ? 'sd-smart-filters__toggle--active' : ''}`}
            >
                <SlidersHorizontal size={14} />
                {t('systemDashboard.common.filters')}
                {hasActiveSmartFilters && !isOpen && (
                    <span className="sd-smart-filters__badge">!</span>
                )}
            </button>

            {isOpen && (
                <div className="sd-smart-filters__panel">
                    <div className="sd-smart-filters__grid">
                        {activeTab === 'users' && (
                            <>
                                <FilterSelect
                                    label={t('systemDashboard.users.gender')}
                                    value={filters.gender}
                                    options={[
                                        { value: 'male', label: t('systemDashboard.users.male') },
                                        { value: 'female', label: t('systemDashboard.users.female') }
                                    ]}
                                    onChange={(val) => onChange({ ...filters, gender: val })}
                                />
                                <FilterSelect
                                    label={t('systemDashboard.users.country')}
                                    value={filters.country_id}
                                    options={countryOptions}
                                    onChange={(val) => onChange({ ...filters, country_id: val })}
                                />
                                <FilterInput
                                    label={t('systemDashboard.users.minAge')}
                                    type="number"
                                    value={filters.min_age}
                                    onChange={(val) => onChange({ ...filters, min_age: val })}
                                />
                                <FilterInput
                                    label={t('systemDashboard.users.maxAge')}
                                    type="number"
                                    value={filters.max_age}
                                    onChange={(val) => onChange({ ...filters, max_age: val })}
                                />
                            </>
                        )}

                        {activeTab === 'social' && (
                            <>
                                <FilterInput
                                    label={t('systemDashboard.social.minReactions')}
                                    type="number"
                                    value={filters.min_reactions}
                                    onChange={(val) => onChange({ ...filters, min_reactions: val })}
                                />
                                <FilterInput
                                    label={t('systemDashboard.social.minComments')}
                                    type="number"
                                    value={filters.min_comments}
                                    onChange={(val) => onChange({ ...filters, min_comments: val })}
                                />
                            </>
                        )}

                        {activeTab === 'medical' && (
                            <>
                                <FilterSelect
                                    label={t('systemDashboard.medical.visitType')}
                                    value={filters.visit_type}
                                    options={[
                                        { value: 'inpatient', label: t('systemDashboard.medical.inpatient') },
                                        { value: 'outpatient', label: t('systemDashboard.medical.outpatient') }
                                    ]}
                                    onChange={(val) => onChange({ ...filters, visit_type: val })}
                                />
                            </>
                        )}
                    </div>

                    <div className="sd-smart-filters__footer">
                        {hasActiveSmartFilters ? (
                            <span className="sd-smart-filters__status">
                                {t('systemDashboard.common.filtersActive')}
                            </span>
                        ) : (
                            <span />
                        )}
                        <button
                            onClick={onReset}
                            className="sd-smart-filters__reset"
                        >
                            <X size={10} />
                            {t('systemDashboard.common.reset')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SmartFilters;
